import { makeAutoObservable, runInAction } from 'mobx'
import { type Integration, type ISettingRequestBySettingDict } from 'entities/Integrations'
import type { IntegrationCustomSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationCustomSettingsStore'
import {
  IntegrationSyncType,
  GET_SYNC_TYPE_DICT,
  GET_SYNC_TYPE_OPTIONS,
} from 'pages/settings/pages/integrations/pages/integrationSettings/lib'

export class IntegrationSettingsSyncStore<TSettingsDict extends object> {
  syncType: IntegrationSyncType = IntegrationSyncType.oneWay

  constructor(
    private _integration: Integration,
    private _customSetting: IntegrationCustomSettingsStore<TSettingsDict>,
    private _syncCheckboxKeys: string[],
    private _expandedCheckboxHandler?: (type: string, value: boolean) => string[]
  ) {
    makeAutoObservable(this)
  }

  init() {
    const timeout = setInterval(() => {
      if (this._customSetting.status.isSuccess) {
        clearInterval(timeout)

        if (
          this._syncCheckboxKeys.some((key) =>
            Boolean(this._customSetting.getValue(key as keyof TSettingsDict))
          )
        ) {
          runInAction(() => {
            this.syncType = IntegrationSyncType.twoWay
          })
        }
      }
    }, 200)
  }

  updateSyncData = (keys: string[], value: boolean) => {
    const data = keys.map((key) => ({
      key,
      value: value ? 1 : 0,
    })) as ISettingRequestBySettingDict<TSettingsDict>

    this._customSetting.patchData(data)
  }

  setSyncType = (type: IntegrationSyncType) => {
    this.syncType = type

    if (
      type === IntegrationSyncType.oneWay &&
      this._syncCheckboxKeys.some((key) =>
        Boolean(this._customSetting.getValue(key as keyof TSettingsDict))
      )
    ) {
      this.updateSyncData(this._syncCheckboxKeys, false)
    }
  }

  setSyncType1_0 = async (type: IntegrationSyncType) => {
    this.syncType = type

    if (
      type === IntegrationSyncType.oneWay &&
      this._syncCheckboxKeys.some((key) =>
        Boolean(this._customSetting.getValue(key as keyof TSettingsDict))
      )
    ) {
      this.updateSyncData(this._syncCheckboxKeys, false)

      for (const key of this._syncCheckboxKeys) {
        await this._customSetting.patchSyncData1_0(key, false)
      }
    }
  }

  setCheckbox = (type: string) => async (value: boolean) => {
    this.updateSyncData([type], value)
  }

  setExpandedCheckbox = (type: string) => async (value: boolean) => {
    if (this._expandedCheckboxHandler) {
      const keysToUpdate = this._expandedCheckboxHandler(type, value)
      this.updateSyncData(keysToUpdate, value)
    } else {
      this.updateSyncData([type], value)
    }
  }

  getCheckbox = (type: string) => {
    return Boolean(this._customSetting.getValue(type as keyof TSettingsDict))
  }

  setCheckbox1_0 = (type: string) => async (value: boolean) => {
    this._customSetting.patchSyncData1_0(type, value)
  }

  get isTwoWaySync() {
    return this.syncType === IntegrationSyncType.twoWay
  }

  get getLabel() {
    return GET_SYNC_TYPE_DICT(this._integration.name)[this.syncType]
  }

  get selectItems() {
    return GET_SYNC_TYPE_OPTIONS(this._integration.name)
  }

  get integrationName() {
    return this._integration.name
  }
}
